import { useToast } from '@components/index'
import { usePromoteToPartner } from '@services/referral'
import { useClients } from '@services/users/queries/useClients'
import { UserPlus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface PromotePartnerModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  userId?: string
}

export function PromotePartnerModal({
  open,
  onClose,
  onSuccess,
  userId
}: PromotePartnerModalProps) {
  const { t } = useTranslation('admin')
  const toast = useToast()
  const promoteMutation = usePromoteToPartner()

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    userId
  )
  const [rewardAmount, setRewardAmount] = useState('')
  const [customCode, setCustomCode] = useState('')
  const [errors, setErrors] = useState<{
    user?: string
    reward?: string
  }>({})

  const { data: usersData, isLoading: isLoadingUsers } = useClients({
    params: {
      search: searchTerm,
      limit: 20,
      is_partner: false
    }
  })

  useEffect(() => {
    if (open) {
      setSelectedUserId(userId)
      setRewardAmount('')
      setCustomCode('')
      setSearchTerm('')
      setErrors({})
    }
  }, [open, userId])

  const userOptions: ComboboxOption[] = useMemo(
    () =>
      (usersData?.results ?? []).map((user) => ({
        value: String(user.id),
        label:
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name} (${user.email})`
            : user.email ?? String(user.id)
      })),
    [usersData]
  )

  const handleSubmit = async () => {
    const nextErrors: { user?: string; reward?: string } = {}
    if (!selectedUserId) {
      nextErrors.user = t('referrals.promote.userIdRequired')
    }
    const reward = parseFloat(rewardAmount)
    if (rewardAmount === '' || isNaN(reward)) {
      nextErrors.reward = t('referrals.promote.rewardAmountRequired')
    } else if (reward < 0) {
      nextErrors.reward = t('referrals.promote.rewardAmountMin')
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    try {
      await promoteMutation.mutateAsync({
        body: {
          user_id: selectedUserId!,
          reward_amount: reward,
          ...(customCode.trim() ? { custom_code: customCode.trim() } : {})
        }
      })
      toast.success({ message: t('referrals.promote.success') })
      onClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error({
        message:
          error?.response?.data?.detail ||
          error?.message ||
          t('referrals.promote.error')
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <UserPlus className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-1">
              <DialogTitle>{t('referrals.promote.title')}</DialogTitle>
              <DialogDescription>
                {t('referrals.promote.description')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">
              {t('referrals.promote.userId')}
              <span className="ml-0.5 text-destructive">*</span>
            </Label>
            {userId ? (
              <Input
                value={userId}
                disabled
                placeholder={t('referrals.promote.userIdPlaceholder')}
              />
            ) : (
              <Combobox
                value={selectedUserId}
                onChange={(value) => {
                  setSelectedUserId(value)
                  setErrors((prev) => ({ ...prev, user: undefined }))
                }}
                onSearch={setSearchTerm}
                options={userOptions}
                loading={isLoadingUsers}
                placeholder={t('referrals.promote.userIdPlaceholder')}
              />
            )}
            {errors.user && (
              <p className="mt-1.5 text-sm font-medium text-destructive">
                {errors.user}
              </p>
            )}
          </div>

          <div>
            <Label className="mb-1.5 block">
              {t('referrals.promote.rewardAmount')}
              <span className="ml-0.5 text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                type="number"
                min={0}
                step="0.01"
                value={rewardAmount}
                onChange={(e) => {
                  setRewardAmount(e.target.value)
                  setErrors((prev) => ({ ...prev, reward: undefined }))
                }}
                placeholder={t('referrals.promote.rewardAmountPlaceholder')}
                className="pr-9"
              />
              <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                €
              </span>
            </div>
            {errors.reward && (
              <p className="mt-1.5 text-sm font-medium text-destructive">
                {errors.reward}
              </p>
            )}
          </div>

          <div>
            <Label className="mb-1.5 block">
              {t('referrals.promote.customCode')}
            </Label>
            <Input
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value)}
              placeholder={t('referrals.promote.customCodePlaceholder')}
              maxLength={20}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t('referrals.promote.customCodeExtra')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghostDark" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={promoteMutation.isPending}>
            <UserPlus className="h-4 w-4" />
            {t('referrals.promote.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
